import { useEffect, useState } from 'react'
import { useLocation, useNavigate, type Location } from 'react-router-dom'
import { X } from 'lucide-react'
import { openSignIn } from '@/lib/signin-navigation'
import { openVerifyIdentity } from '@/lib/verify-identity-navigation'
import { LetterPackPurchaseModal } from '@/components/letters/LetterPackPurchaseModal'
import { InstallAppPrompt } from '@/components/pwa/InstallAppPrompt'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { authErrorMessage } from '@/lib/auth-errors'
import { hardDeleteUserAccount, sendVerificationEmail } from '@/lib/callables'
import { cn } from '@/lib/utils'

/**
 * Settings overlay — account controls over the current screen.
 * Opened via `/settings` while the previous route stays underneath.
 */
export function SettingsModal() {
  const { user, profile, isGuest, isLoading, logout, isSigningOut, refreshUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [entered, setEntered] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [buyPackOpen, setBuyPackOpen] = useState(false)

  const background =
    (location.state as { background?: Location } | null)?.background ?? null

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEntered(true))
    })
    return () => {
      document.body.style.overflow = prev
      window.cancelAnimationFrame(id)
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (confirmDelete) setConfirmDelete(false)
      else dismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete])

  function dismiss() {
    setEntered(false)
    const target = background ?? { pathname: '/search', search: '', hash: '', state: null }
    window.setTimeout(() => {
      navigate(
        { pathname: target.pathname, search: target.search, hash: target.hash },
        { replace: true, state: target.state },
      )
    }, 200)
  }

  async function onLogout() {
    setBusy(true)
    setError(null)
    try {
      await logout()
      setEntered(false)
      window.setTimeout(() => navigate('/search', { replace: true }), 200)
    } catch (e) {
      setError(authErrorMessage(e))
      setBusy(false)
    }
  }

  async function onResendVerification() {
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      await sendVerificationEmail()
      setInfo('Verification email sent. Check your inbox.')
    } catch (e) {
      setError(authErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onRefreshEmailStatus() {
    setBusy(true)
    setError(null)
    try {
      await refreshUser()
      setInfo('Email status refreshed.')
    } catch (e) {
      setError(authErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onConfirmDelete() {
    setBusy(true)
    setError(null)
    try {
      await hardDeleteUserAccount()
      await logout()
      setEntered(false)
      window.setTimeout(() => navigate('/search', { replace: true }), 200)
    } catch (e) {
      setError(authErrorMessage(e))
      setBusy(false)
      setConfirmDelete(false)
    }
  }

  function openIdentityVerify() {
    const underlay = background ?? {
      ...location,
      pathname: '/search',
      search: '',
      hash: '',
      state: null,
    }
    openVerifyIdentity(navigate, underlay as Location, '/settings')
  }

  const emailVerified = Boolean(user?.emailVerified || profile?.emailVerified)
  const identityVerified = Boolean(profile?.isIdentityVerified)

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-end justify-center bg-navy/70 p-0 backdrop-blur-sm transition-opacity duration-200 ease-out sm:items-center sm:p-4',
        entered ? 'opacity-100' : 'opacity-0',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !confirmDelete) dismiss()
      }}
    >
      <Card
        className={cn(
          'relative flex max-h-[min(90dvh,720px)] w-full max-w-lg flex-col overflow-hidden border-0 shadow-2xl transition-all duration-200 ease-out sm:rounded-2xl',
          'rounded-t-2xl rounded-b-none sm:rounded-b-2xl',
          entered ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-[0.98] opacity-0',
        )}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-end p-3">
          <button
            type="button"
            onClick={dismiss}
            className="pointer-events-auto rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
            aria-label="Close settings"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <CardHeader className="shrink-0 px-5 pb-2 pt-5 pr-12">
          <CardTitle id="settings-title">Settings</CardTitle>
          <CardDescription>Account and email verification.</CardDescription>
        </CardHeader>

        <CardContent className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-6">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : !user || isGuest ? (
            <div className="space-y-3">
              <p className="text-sm text-muted">
                Sign in to manage your account, verify email, and use Biddles, chat, and letters.
              </p>
              <Button
                variant="primary"
                onClick={() => openSignIn(navigate, background ?? location, '/settings')}
              >
                Sign in
              </Button>
            </div>
          ) : (
            <>
              <section className="space-y-1 text-sm">
                <h3 className="text-base font-semibold text-navy">Profile</h3>
                <Row label="Name" value={profile?.displayName || user.displayName || 'Not set'} />
                <Row label="Email" value={profile?.email || user.email || '—'} />
                <Row label="Email verified" value={emailVerified ? 'Yes' : 'No'} />
                {identityVerified ? (
                  <Row label="Identity-verified" value="Yes" />
                ) : (
                  <div className="space-y-2 border-b border-border py-3 last:border-0">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={openIdentityVerify}
                    >
                      Verify your identity
                    </Button>
                    <p className="text-xs leading-relaxed text-muted">
                      You won&apos;t be able to create Biddles or register as the owner of your
                      property until you have verified your identity.
                    </p>
                  </div>
                )}
              </section>

              {!emailVerified ? (
                <section className="space-y-3 rounded-xl border border-border bg-surface/60 p-4">
                  <div>
                    <h3 className="text-sm font-semibold text-navy">Verify your email</h3>
                    <p className="mt-1 text-xs text-muted">
                      Email/password accounts need a verified email before some owner and letter
                      flows.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => void onResendVerification()}
                    >
                      {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                      Resend verification email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => void onRefreshEmailStatus()}
                    >
                      I already verified
                    </Button>
                  </div>
                </section>
              ) : null}

              <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-4">
                <div>
                  <h3 className="text-sm font-semibold text-navy">Letter credits</h3>
                  <p className="mt-0.5 text-xs text-muted">
                    Balance: {profile?.letterCredits ?? 0} credit
                    {(profile?.letterCredits ?? 0) === 1 ? '' : 's'}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setBuyPackOpen(true)}
                >
                  Buy letter pack
                </Button>
              </section>

              <InstallAppPrompt force />

              <section className="rounded-xl border border-border p-4">
                <h3 className="text-sm font-semibold text-navy">Coming later</h3>
                <p className="mt-1 text-xs text-muted">
                  Web push notification settings will appear here.
                </p>
              </section>

              {error ? <p className="text-sm text-danger">{error}</p> : null}
              {info ? <p className="text-sm text-success">{info}</p> : null}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  variant="outline"
                  disabled={busy || isSigningOut}
                  onClick={() => void onLogout()}
                >
                  Sign out
                </Button>
                <Button
                  variant="outline"
                  disabled={busy || isSigningOut}
                  className="border-danger text-danger hover:bg-danger/5 hover:text-danger"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete account
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <LetterPackPurchaseModal
        open={buyPackOpen}
        onClose={() => setBuyPackOpen(false)}
        onPurchased={() => setBuyPackOpen(false)}
      />

      {confirmDelete ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-navy/50 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
          aria-describedby="delete-account-desc"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !busy) setConfirmDelete(false)
          }}
        >
          <Card className="w-full max-w-sm border-0 shadow-2xl">
            <CardHeader className="pb-2">
              <CardTitle id="delete-account-title" className="text-lg">
                Delete account?
              </CardTitle>
              <CardDescription id="delete-account-desc">
                Permanently delete your Biddle account? This removes your data and cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => void onConfirmDelete()}
              >
                {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                Delete account
              </Button>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-muted">{label}</span>
      <span className="text-right font-medium text-navy">{value}</span>
    </div>
  )
}
